import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchLiceReportWeeks, type LiceReportWeekDto } from './liceReportClient';

/**
 * The ISO weeks a lice report can show, newest first, including the running week.
 *
 * Deliberately not gated on `useLiceSource()`. The list does not depend on which
 * implementation builds a week, and waiting for the flag would hold the week selector back.
 */
const LICE_REPORT_WEEKS_KEY = '/api/v2/client/internal/{clientId}/lice/weeklyReport/weeks';

const NO_WEEKS: LiceReportWeekDto[] = [];

export interface UseGetLiceReportWeeksResult {
    data: LiceReportWeekDto[];
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => unknown;
}

export function useGetLiceReportWeeks(clientId: number): UseGetLiceReportWeeksResult {
    const query = useQuery({
        queryKey: [LICE_REPORT_WEEKS_KEY, clientId],
        queryFn: () => fetchLiceReportWeeks(clientId),
    });

    const data = useMemo(() => query.data?.data.weeks ?? NO_WEEKS, [query.data?.data.weeks]);

    return {
        data,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        error: query.error,
        refetch: query.refetch,
    };
}
